/**
 * Rules: when a narration looks like this, it is probably that.
 *
 * A suggestion engine, not a posting engine. A rule sets a category and a
 * confidence and never posts, which is the whole reason automatic
 * categorisation is safe to have: the worst a bad rule can do is mislabel a
 * line somebody then corrects.
 *
 * Writing rules is the Admin's and running them is the Book Keeper's. That is
 * not an oversight — the person deciding what a narration means should not be
 * the same one deciding it line by line under time pressure.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type BankCategory } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Field, Loading, Note } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface Rule {
  id: string
  priority: number
  matcher: string
  pattern: string
  direction: string | null
  category_id: string
  category_name: string | null
  confidence: number
  hit_count: number
  is_active: boolean
  clears_review: boolean
}

const MATCHERS = [
  { value: 'contains', label: 'The narration contains', hint: 'the ordinary choice' },
  { value: 'counterparty', label: 'The payee name contains', hint: '' },
  { value: 'vpa', label: 'The UPI address contains', hint: '' },
  { value: 'regex', label: 'A pattern matches', hint: 'for when the others cannot' },
]

const DIRECTIONS = [
  { value: '', label: 'Money in or out' },
  { value: 'debit', label: 'Money out only' },
  { value: 'credit', label: 'Money in only' },
]

export default function Rules() {
  const { can } = useAuth()
  const queryClient = useQueryClient()

  const rules = useQuery({ queryKey: ['rules'], queryFn: () => api.get<Rule[]>('/banking/rules') })
  const categories = useQuery({
    queryKey: ['bank-categories'],
    queryFn: () => api.get<BankCategory[]>('/banking/categories'),
  })

  const [matcher, setMatcher] = useState('contains')
  const [pattern, setPattern] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [direction, setDirection] = useState('')
  const [confidence, setConfidence] = useState(95)
  const [priority, setPriority] = useState(100)
  const [loaded, setLoaded] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post<Rule>('/banking/rules', {
        matcher,
        pattern: pattern.trim(),
        category_id: categoryId,
        direction: direction || null,
        confidence,
        priority,
      }),
    onSuccess: () => {
      setPattern('')
      void queryClient.invalidateQueries({ queryKey: ['rules'] })
    },
  })

  const toggle = useMutation({
    mutationFn: (vars: { id: string; is_active: boolean }) =>
      api.patch<Rule>(`/banking/rules/${vars.id}`, { is_active: vars.is_active }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['rules'] }),
  })

  const starter = useMutation({
    mutationFn: () =>
      api.post<{ categories: number; rules: number }>('/banking/rules/starter-pack', {}),
    onSuccess: (out) => {
      setLoaded(`${out.rules} rules across ${out.categories} categories`)
      void queryClient.invalidateQueries({ queryKey: ['rules'] })
      void queryClient.invalidateQueries({ queryKey: ['bank-categories'] })
    },
  })

  const rows = rules.data ?? []
  const fired = useMemo(() => rows.filter((r) => r.hit_count > 0).length, [rows])

  if (rules.isLoading) return <Loading what="Loading rules" />

  const canWrite = can('banking.rule.configure')
  const ready = pattern.trim().length > 0 && categoryId

  return (
    <>
      <ErrorNote error={rules.error ?? create.error ?? starter.error ?? toggle.error} />
      {loaded && <Note kind="ok">Loaded {loaded}.</Note>}

      {rows.length === 0 && (
        <Note kind="info">
          No rules yet, so every imported line waits for somebody. The starter pack below
          covers the merchants and statutory payees whose name in a narration means one
          thing and not another — food delivery, fuel, telecom, income tax, bank charges.
          Nothing in it matches a person&rsquo;s name, because nothing in a person&rsquo;s
          name says what the money was for.
        </Note>
      )}

      {canWrite && rows.length === 0 && (
        <Card title="Start from a standard set" hint="you can turn any of them off afterwards">
          <button
            className="primary"
            disabled={starter.isPending}
            onClick={() => starter.mutate()}
          >
            {starter.isPending ? 'Loading…' : 'Load the starter pack'}
          </button>
        </Card>
      )}

      {canWrite && (
        <Card title="Add a rule" hint="tried in order; the first that fits wins">
          <div className="row">
            <Field label="When">
              <select value={matcher} onChange={(e) => setMatcher(e.target.value)}>
                {MATCHERS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                    {m.hint ? ` — ${m.hint}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="This" caption="case and punctuation do not matter">
              <input
                className="mono"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                placeholder="zomato"
              />
            </Field>
            <Field label="On">
              <select value={direction} onChange={(e) => setDirection(e.target.value)}>
                {DIRECTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="row">
            <Field label="Then it is">
              <SearchSelect
                aria-label="Then it is"
                value={categoryId}
                onChange={setCategoryId}
                placeholder="Choose a category…"
                options={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              />
            </Field>
            <Field
              label="How sure"
              caption="80 or more categorises on its own; below it, a person still agrees"
            >
              <input
                type="number"
                min={1}
                max={100}
                value={confidence}
                onChange={(e) => setConfidence(Number(e.target.value))}
              />
            </Field>
            <Field label="Order" caption="lower runs first; specific beats general">
              <input
                type="number"
                min={1}
                max={1000}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
              />
            </Field>
          </div>

          {confidence < 80 && pattern.trim() && (
            <Note kind="info">
              Below 80, so a hit fills in the category and still leaves the line waiting for
              somebody. Right for a name a vendor and an employee could share.
            </Note>
          )}

          <button className="primary" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add this rule'}
          </button>
        </Card>
      )}

      {rows.length > 0 && (
        <Card
          title="Rules"
          hint={`${rows.length} rules, ${fired} of which have ever fired`}
          tight
        >
          <table>
            <thead>
              <tr>
                <th className="num">Order</th>
                <th>When</th>
                <th>Then</th>
                <th>On</th>
                <th className="num">Sure</th>
                <th className="num">Used</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={r.is_active ? undefined : { opacity: 0.5 }}>
                  <td className="num muted">{r.priority}</td>
                  <td>
                    <span className="muted">{r.matcher}</span>{' '}
                    <span className="mono">{r.pattern}</span>
                  </td>
                  <td>{r.category_name ?? <span className="faint">—</span>}</td>
                  <td className="muted">{r.direction ?? 'either'}</td>
                  <td className="num">
                    {r.confidence}
                    {!r.clears_review && <div className="faint">asks first</div>}
                  </td>
                  <td className="num">
                    {r.hit_count > 0 ? r.hit_count : <span className="faint">never</span>}
                  </td>
                  {canWrite && (
                    <td className="num">
                      <button
                        className="small"
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate({ id: r.id, is_active: !r.is_active })}
                      >
                        {r.is_active ? 'Turn off' : 'Turn on'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <PaymentRequestRules
        categories={categories.data ?? []}
        canWrite={canWrite}
        onDone={() => void queryClient.invalidateQueries({ queryKey: ['bank-categories'] })}
      />

      <Card title="What a rule can and cannot do">
        <p className="muted" style={{ marginBottom: 0 }}>
          A rule sets a category and how sure it is. It never posts, and it never replaces a
          category somebody chose — a rule quietly overruling a person would make every
          correction provisional. Rules are tried in order and the first that fits wins,
          rather than a scoring contest whose outcome nobody could predict. Run them from
          Bank statements; they also run on their own as a statement is imported.
        </p>
      </Card>
    </>
  )
}


/**
 * Which spending needs an approved payment request before the money leaves.
 *
 * The bank debit control flags a debit in a category that needs one and has
 * none. Turning it off for a category is the allowlist -- bank charges, interest
 * the bank debits on its own -- and it is configuration, so it is Admin's.
 */
function PaymentRequestRules({
  categories,
  canWrite,
  onDone,
}: {
  categories: BankCategory[]
  canWrite: boolean
  onDone: () => void
}) {
  const change = useMutation({
    mutationFn: ({ id, requires }: { id: string; requires: boolean }) =>
      api.put(`/banking/categories/${id}/payment-request-rule`, {
        requires_payment_request: requires,
      }),
    onSuccess: onDone,
  })
  const spending = categories.filter((c) => c.is_active && c.direction !== 'credit')
  if (spending.length === 0) return null
  const exempt = spending.filter((c) => !c.requires_payment_request)

  return (
    <Card
      title="Which spending needs a payment request"
      hint={`${exempt.length} of ${spending.length} exempt`}
      tight
    >
      <ErrorNote error={change.error} />
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Needs an approved request</th>
            {canWrite && <th />}
          </tr>
        </thead>
        <tbody>
          {spending.map((c) => (
            <tr key={c.id}>
              <td>
                {c.name} <span className="faint mono">{c.code}</span>
              </td>
              <td>
                {c.requires_payment_request ? 'Yes' : <span className="faint">No — exempt</span>}
              </td>
              {canWrite && (
                <td className="num">
                  <button
                    className="small"
                    disabled={change.isPending}
                    onClick={() =>
                      change.mutate({ id: c.id, requires: !c.requires_payment_request })
                    }
                  >
                    {c.requires_payment_request ? 'Exempt' : 'Require'}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}
